/**
 * Utilitaires partagés du parcours Santé Mobilité.
 *
 * Centralisation volontaire : avant ce fichier, `computeMonthsCount` était
 * dupliqué dans la barre de prix (formule `ceil(diffDays/30)`, depuis
 * remplacée par PriceHeadline) ET dans
 * FinalizationStep (formule legacy moment `parseInt(diff('months', true)) + 1`).
 * Les deux étaient buggées ET divergeaient l'un de l'autre, ce qui pouvait
 * donner un prix dans la sticky différent du prix au paiement.
 *
 * Tous les callers DOIVENT utiliser cette fonction pour rester cohérents avec
 * le back. Tout changement de la formule ici doit être fait en coordination
 * stricte avec le back (cf. /payments/price/health_mobility et endpoints
 * /subscription/sante-mobilite/{price,session} côté Symfony).
 */

/** Nombre moyen de jours par mois sur une année (365.25 / 12). */
const DAYS_PER_MONTH = 30.4375;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Premier jour de départ acceptable : DEMAIN, à minuit local.
 *
 * Un contrat ne peut pas prendre effet le jour même. Le jour courant était
 * pourtant sélectionnable des DEUX côtés — `disablePast` n'exclut que les
 * jours révolus — et même pré-rempli par défaut à l'étape 1.
 *
 * Ici et non dans un des deux écrans : la date de départ se saisit à l'étape 1
 * (`ContractDatesStep`) ET se réédite à l'étape finale (`EditableRecapCard`),
 * qui portaient déjà chacun leur copie des helpers de date. Une règle posée
 * dans un seul des deux serait contournable par l'autre.
 */
export const startOfTomorrow = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 1);
  return d;
};

/**
 * Normalise une Date au début du jour en UTC. Le back fait la même
 * normalisation (`setTime(0, 0)` côté Symfony sur le timestamp UTC), donc on
 * doit faire pareil ici pour que `diffDays` soit identique des deux côtés.
 *
 * Important : on utilise les composants UTC de la date pour ne pas être
 * affecté par le fuseau du navigateur. Une startDate stockée à `2026-06-26T00:00:00.000Z`
 * doit donner `26` peu importe que l'utilisateur soit à Paris ou New York.
 */
const utcMidnight = (d: Date): Date => {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0),
  );
};

/**
 * Calcule le nombre de mois entamés couverts par l'intervalle [startDate, endDate].
 *
 * Règle métier (alignée à la virgule près avec le back) :
 *   monthsCount = ceil((diffDays + 1) / 30.4375)
 *
 * Avec :
 *   - `diffDays = endUtcMidnight - startUtcMidnight` en jours entiers
 *   - `+1` parce que `endDate` est le **dernier jour de couverture** (inclusif).
 *     Une couverture du 1er au 1er janvier = 1 jour de couverture, pas 0.
 *
 * Exemples canoniques :
 *   - 26/06/2026 → 25/07/2026 : 30 jours couverts → ceil(30/30.4375) = 1 mois
 *   - 26/06/2026 → 26/07/2026 : 31 jours couverts → ceil(31/30.4375) = 2 mois
 *   - 01/01/2026 → 31/12/2026 : 365 jours couverts → ceil(365/30.4375) = 12 mois
 *   - 25/07/2026 → 24/07/2027 : 365 jours couverts → 12 mois
 *   - même jour : 1 jour couvert → 1 mois (min)
 *
 * @returns nombre de mois entamés, 0 si l'une des dates est invalide.
 */
export const computeMonthsCount = (
  startISO: string | null | undefined,
  endISO: string | null | undefined,
): number => {
  if (!startISO || !endISO) return 0;
  const start = new Date(startISO);
  const end = new Date(endISO);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
  const diffDays = Math.round(
    (utcMidnight(end).getTime() - utcMidnight(start).getTime()) / MS_PER_DAY,
  );
  if (diffDays < 0) return 0;
  // +1 car endDate est inclusif : couverture du 1er au 1er = 1 jour couvert.
  return Math.max(1, Math.ceil((diffDays + 1) / DAYS_PER_MONTH));
};
